import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { UtensilsCrossed, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const restaurants = [
  { nameKey: 'restBoatyard', descKey: 'restBoatyardDesc', price: '$$', distanceMi: 1, url: 'https://www.boatyardbarandgrill.com/' },
  { nameKey: 'restCarrolsCreek', descKey: 'restCarrolsCreekDesc', price: '$$$', distanceMi: 1.5, url: 'https://carrolscreek.com/' },
  { nameKey: 'restVin909', descKey: 'restVin909Desc', price: '$$', distanceMi: 1, url: 'https://vin909.com/' },
  { nameKey: 'restOlearys', descKey: 'restOlearysDesc', price: '$$$$', distanceMi: 2, url: 'https://olearysseafood.com/' },
  { nameKey: 'restChickRuths', descKey: 'restChickRuthsDesc', price: '$', distanceMi: 2, url: 'https://www.chickandruths.com/' },
  { nameKey: 'restMissShirleys', descKey: 'restMissShirleysDesc', price: '$$', distanceMi: 4, url: 'https://www.missshirleys.com/' },
  { nameKey: 'restMiddletonTavern', descKey: 'restMiddletonTavernDesc', price: '$$$', distanceMi: 2, url: 'https://www.middletontavern.com/' },
  { nameKey: 'restChoptank', descKey: 'restChoptankDesc', price: '$$$', distanceMi: 2, url: 'https://www.thechoptank.com/' },
  { nameKey: 'restIronRooster', descKey: 'restIronRoosterDesc', price: '$$', distanceMi: 2, url: 'https://www.ironroosterusa.com/' },
  { nameKey: 'rest49West', descKey: 'rest49WestDesc', price: '$$', distanceMi: 2, url: 'https://www.49westcoffeehouse.com/' },
];

const formatDistance = (mi, language) =>
  language === 'fr' ? `${(mi * 1.60934).toFixed(1)} km` : `${mi} mi`;

export const WhereToEat = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-20 bg-white" data-testid="eat-section">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }} className="text-center mb-12">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A2B3D] mb-4">{t('whereToEatTitle')}</h2>
          <div className="wave-divider mb-6" />
          <p className="text-[#6B8299] max-w-2xl mx-auto">{t('whereToEatDesc')}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }} className="grid md:grid-cols-2 gap-4">
          {restaurants.map((r) => (
            <div key={r.nameKey} className="border border-[#CBD5E1] rounded-sm p-5 hover:border-[#1E3A5F] transition-colors bg-white"
              data-testid={`restaurant-${r.nameKey}`}>
              <div className="flex items-center gap-2 mb-2">
                <UtensilsCrossed className="w-4 h-4 text-[#C9A96E]" />
                <h4 className="font-serif text-lg text-[#1A2B3D]">{t(r.nameKey)}</h4>
              </div>
              <p className="text-[#6B8299] text-sm mb-3 leading-relaxed">{t(r.descKey)}</p>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="flex items-center gap-1 text-[#1E3A5F]">
                  <MapPin className="w-3 h-3" /> {formatDistance(r.distanceMi, language)} {t('fromVenue')}
                </span>
                <span className="text-[#C9A96E] font-medium">{r.price}</span>
                <a href={r.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[#1E3A5F] hover:text-[#C9A96E] transition-colors ml-auto">
                  {t('visitWebsite')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
